import {
  workspaceStorageUsageResponseSchema,
  type WorkspaceStorageUsageResponse
} from '@inktap/contracts/workspace-storage-usage';

import { API_BASE_URL } from './api';

export type StorageUsageScope = WorkspaceStorageUsageResponse['scope'];
export type StorageUsageFailure = 'authentication' | 'access' | 'invalid' | 'timeout' | 'unavailable' | 'cancelled';

const messages: Record<StorageUsageFailure, string> = {
  authentication: 'Sign in to view storage usage.',
  access: 'Current workspace owner access is required to view storage usage.',
  invalid: 'Unable to confirm storage usage. Refresh to try again.',
  timeout: 'Storage usage took too long to load. Refresh to try again.',
  unavailable: 'Storage usage is unavailable. Refresh to try again.',
  cancelled: 'Storage usage request cancelled.'
};

export class StorageUsageReadError extends Error {
  constructor(readonly reason: StorageUsageFailure) {
    super(messages[reason]);
    this.name = 'StorageUsageReadError';
  }
}

export const STORAGE_USAGE_DEADLINE_MS = 15_000;
const MAX_RECEIPT_BYTES = 16_384;

export function exactBytes(value: string): string {
  // Only canonical contract decimals enter this formatter; never convert bytes to Number.
  if (!/^(0|[1-9][0-9]*)$/.test(value) || value.length > 40) throw new StorageUsageReadError('invalid');
  return `${BigInt(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} bytes`;
}

/** Rounded binary units for scanning; exact bytes remain the authoritative value. */
export function approximateBytes(value: string): string | null {
  exactBytes(value); // Apply the same canonical decimal and length validation.
  const bytes = BigInt(value);
  if (bytes < 1024n) return null;
  const units = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let divisor = 1024n;
  let unit = 0;
  while (unit < units.length - 1 && bytes >= divisor * 1024n) {
    divisor *= 1024n;
    unit += 1;
  }
  let hundredths = (bytes * 100n + divisor / 2n) / divisor;
  if (hundredths >= 102400n && unit < units.length - 1) {
    divisor *= 1024n;
    unit += 1;
    hundredths = (bytes * 100n + divisor / 2n) / divisor;
  }
  const whole = (hundredths / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fraction = (hundredths % 100n).toString().padStart(2, '0').replace(/0+$/, '');
  return `${whole}${fraction ? '.' + fraction : ''} ${units[unit]}`;
}

async function readBoundedJson(response: Response, controller: AbortController): Promise<unknown> {
  const reader = response.body?.getReader();
  if (!reader) throw new StorageUsageReadError('invalid');
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > MAX_RECEIPT_BYTES) {
        controller.abort();
        throw new StorageUsageReadError('invalid');
      }
      chunks.push(value);
    }
    const bytes = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)) as unknown;
  } finally {
    reader.releaseLock();
  }
}

export async function readWorkspaceStorageUsage({
  signal, expectedScope
}: { signal: AbortSignal; expectedScope?: StorageUsageScope }): Promise<WorkspaceStorageUsageResponse> {
  if (signal.aborted) throw new StorageUsageReadError('cancelled');
  const controller = new AbortController();
  let deadlineReached = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: () => void = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    onAbort = () => {
      controller.abort();
      reject(new StorageUsageReadError('cancelled'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    timer = setTimeout(() => {
      deadlineReached = true;
      controller.abort();
      reject(new StorageUsageReadError('timeout'));
    }, STORAGE_USAGE_DEADLINE_MS);
  });
  const observation = async () => {
    const response = await fetch(`${API_BASE_URL}/me/storage-usage`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal
    });
    // Refusal bodies and network errors may contain private details. Never read or expose them.
    if (response.status === 401) throw new StorageUsageReadError('authentication');
    if (response.status === 403) throw new StorageUsageReadError('access');
    if (!response.ok) throw new StorageUsageReadError('unavailable');
    const receipt = workspaceStorageUsageResponseSchema.safeParse(await readBoundedJson(response, controller));
    if (!receipt.success || (expectedScope && (
      receipt.data.scope.tenantId !== expectedScope.tenantId ||
      receipt.data.scope.workspaceId !== expectedScope.workspaceId
    ))) throw new StorageUsageReadError('invalid');
    return receipt.data;
  };
  try {
    return await Promise.race([observation(), interrupted]);
  } catch (error) {
    if (signal.aborted) throw new StorageUsageReadError('cancelled');
    if (deadlineReached) throw new StorageUsageReadError('timeout');
    if (error instanceof StorageUsageReadError) throw error;
    throw new StorageUsageReadError(error instanceof SyntaxError || error instanceof TypeError && controller.signal.aborted
      ? 'invalid' : 'unavailable');
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', onAbort);
    controller.abort();
  }
}
